# Assets

SEEK provides static assets for third-party integrations.
These are typically SVG images that you can display in your software.

## Displaying images in your software

Certain operations in the SEEK API can return a link to an image asset.
You can use this URL to display the image in your software.

```json
{
  "image": {
    "url": "https://integration.seek.com/awsk/button-assets/button-images/seek/en/standard-accent.svg"
  }
}
```

Use an `img` tag to display the image in your software:

```html
<img
  src="https://integration.seek.com/awsk/button-assets/button-images/seek/en/standard-accent.svg"
  alt="Apply with SEEK"
/>
```

Refer to our use case documentation for the best way to display a given asset in your software.

## Content Security Policy compatibility

This section is for integration partners who enforce strict content security policies in their web-based software.

[Content Security Policy] (CSP) is a security feature that helps prevent various types of attacks, including [Cross-Site Scripting (XSS)](https://owasp.org/www-community/attacks/xss/) and data injection attacks.
Add the following directives to your CSP to allow static assets from the SEEK API:

{/* prettier-ignore */}
```html
Content-Security-Policy:
  connect-src 'self' *.seek.com;
  font-src 'self' https://*.seek.com;
  img-src 'self' https://*.seek.com https://*.seek.com.au;
  script-src 'self' https://integration.seek.com;
  style-src 'self' https://*.seek.com;
```

If your security practice permits use of `default-src`,
this can be simplified with minimal additional risk:

{/* prettier-ignore */}
```html
Content-Security-Policy:
  default-src 'self' *.seek.com *.seek.com.au;
  object-src 'none';
  script-src 'self' https://integration.seek.com;
```

This sample directive instructs the browser to only load images, fonts, scripts, and connect to your own domain (`self`) and SEEK.

[Content Security Policy]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/CSP
